import React, { useState, useEffect } from 'react';
import { useRefresh } from '../RefreshContext';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

export default function Orders({ theme, onOrderClick, tables, user }) {
    const { t } = useLanguage();
    const [orders, setOrders] = useState([]);
    const [closedOrders, setClosedOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [parentOrderNames, setParentOrderNames] = useState({});
    const { refreshKey, refresh } = useRefresh();
    const [showTabs, setShowTabs] = useState(true);
    const [showClosedTabs, setShowClosedTabs] = useState(false);
    const [quickOrderLoading, setQuickOrderLoading] = useState(false);
    const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        async function fetchOrders() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/orders`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch orders');
                const data = await response.json();
                setOrders(data);

                const parentNames = {};
                for (const order of data) {
                    if (order.parent_tab_id) {
                        const parentOrder = data.find(p => p.id === order.parent_tab_id);
                        if (parentOrder && parentOrder.name) {
                            parentNames[order.id] = parentOrder.name.substring(0, 5);
                        }
                    }
                }
                setParentOrderNames(parentNames);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        async function fetchClosedOrders() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/closed-orders`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch closed orders');
                const data = await response.json();
                setClosedOrders(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        fetchClosedOrders();
        fetchOrders();
    }, [refreshKey, t]);

    const handleQuickOrder = async () => {
        const togoTable = tables?.find(t => t.name === 'TOGO');
        if (!togoTable) return;
        setQuickOrderLoading(true);
        try {
            // Generate unique name
            let baseName = 'Cliente';
            let orderName = baseName;
            let counter = 2;
            const openNames = orders.map(o => o.name?.trim().toLowerCase());
            while (openNames.includes(orderName.toLowerCase())) {
                orderName = `${baseName} ${counter}`;
                counter++;
            }
            const response = await fetch(`${BACKEND_URL}/api/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    OrderName: orderName,
                    tableId: togoTable.id,
                    user_id: user?.user_id || user?.id || 1
                })
            });
            if (!response.ok) throw new Error('Failed to create order');
            const data = await response.json();
            refresh();
            if (onOrderClick) onOrderClick(data.id);
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        } finally {
            setQuickOrderLoading(false);
        }
    };

    return (
        <div
            className="orders-component"
            style={{
                background: theme?.colors?.surface || '#fff',
                color: theme?.colors?.text || '#222',
                borderRadius: theme?.borderRadius || 8,
                boxShadow: theme?.boxShadow || '0 2px 8px #0002',
                padding: '.2rem',
                margin: '0rem 0',
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            {tables?.find(tbl => tbl.name === 'TOGO') && (
                <button
                    onClick={handleQuickOrder}
                    disabled={quickOrderLoading}
                    style={{
                        width: '100%',
                        padding: '1rem 1.25rem',
                        marginBottom: '0.5rem',
                        backgroundColor: theme?.colors?.primary || '#1976d2',
                        color: theme?.colors?.textLight || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 8,
                        cursor: quickOrderLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                >
                    {quickOrderLoading ? '...' : `+ ${t('quickOrder') || 'Quick Order'} (TOGO)`}
                </button>
            )}
            <h3
                style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowTabs(v => !v)}
                title={showTabs ? t('hideTabs') : t('showTabs')}
            >
                {t('open')} {t('tabs')}
                <span style={{ marginLeft: 8, fontSize: '1.1em', verticalAlign: 'middle' }}>
                    {showTabs ? '▼' : '▲'}
                </span>
            </h3>
            {loading ? (
                <div>{t('loading')} {t('open').toLowerCase()} {t('tabs').toLowerCase()}...</div>
            ) : error ? (
                <div style={{ color: 'red' }}>{t('error')}: {error}</div>
            ) : orders.length === 0 ? (
                <div style={{ color: theme?.colors?.textSecondary || '#888' }}>{t('no')} {t('open').toLowerCase()} {t('tabs').toLowerCase()} {t('toDisplay')}.</div>
            ) : showTabs ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    padding: 0
                }}>
                    {orders.map(order => (
                        <button
                            key={order.id}
                            style={{
                                padding: '1rem',
                                backgroundColor: theme?.colors?.background || '#f5f5f5',
                                border: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                                borderRadius: theme?.borderRadius || 8,
                                cursor: onOrderClick ? 'pointer' : 'default',
                                transition: 'all 0.2s',
                                textAlign: 'left',
                                fontFamily: 'inherit',
                                fontSize: '0.9rem',
                                minHeight: '80px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                                userSelect: 'none',
                                touchAction: 'manipulation'
                            }}
                            onClick={() => onOrderClick && onOrderClick(order.id)}
                            onTouchStart={(e) => {
                                const touch = e.touches[0];
                                setTouchStartPos({ x: touch.clientX, y: touch.clientY });
                            }}
                            onTouchEnd={(e) => {
                                if (onOrderClick) {
                                    const touch = e.changedTouches[0];
                                    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
                                    const deltaY = Math.abs(touch.clientY - touchStartPos.y);
                                    
                                    // Only trigger if movement was minimal (within 10px)
                                    if (deltaX < 10 && deltaY < 10) {
                                        e.preventDefault();
                                        onOrderClick(order.id);
                                    }
                                }
                            }}
                            onMouseEnter={e => {
                                if (onOrderClick) {
                                    e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#1976d2';
                                    e.currentTarget.style.color = theme?.colors?.surface || '#fff';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                }
                            }}
                            onMouseLeave={e => {
                                if (onOrderClick) {
                                    e.currentTarget.style.backgroundColor = theme?.colors?.background || '#f5f5f5';
                                    e.currentTarget.style.color = theme?.colors?.text || '#222';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }
                            }}
                        >
                            <div style={{ fontWeight: 'bold', fontSize: '1rem', color: theme?.colors?.text || '#222' }}>
                                {order.name}
                                {parentOrderNames[order.id] && <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary }}> ({t('from')} {parentOrderNames[order.id]})</span>}
                            </div>
                            <div style={{ color: theme?.colors?.textSecondary || '#666' }}>
                                {t('tableName')}: {order.table_name}
                            </div>
                            <div style={{ color: theme?.colors?.textSecondary || '#666' }}>
                                {order.item_count || 0} {t('items')}
                            </div>
                            {order.created && (
                                <div style={{ color: theme?.colors?.textSecondary || '#666', fontSize: '0.85rem' }}>
                                    {(() => {
                                        const date = new Date(order.created);
                                        const dateStr = date.toLocaleDateString('en-US', {
                                            day: 'numeric',
                                            month: 'short'
                                        }).replace(',', '').replace(' ', '-').toUpperCase();
                                        const timeStr = date.toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                        });
                                        return `${dateStr} ${timeStr}`;
                                    })()}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            ) : null}

            {/* Closed Tabs Header and Grid */}
            <h3
                style={{ color: theme?.colors?.primary || '#1976d2', marginTop: '0.5rem', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowClosedTabs(v => !v)}
                title={showClosedTabs ? t('hideTabs') : t('showTabs')}
            >
                {t('closed')} {t('tabs')}
                <span style={{ marginLeft: 8, fontSize: '1.1em', verticalAlign: 'middle' }}>
                    {showClosedTabs ? '▼' : '▲'}
                </span>
            </h3>
            {loading ? null : error ? null : closedOrders.length === 0 ? (
                <div style={{ color: theme?.colors?.textSecondary || '#888' }}>{t('no')} {t('closed').toLowerCase()} {t('tabs').toLowerCase()} {t('toDisplay')}.</div>
            ) : showClosedTabs ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    padding: 0
                }}>
                    {closedOrders.map(order => (
                        <button
                            key={order.id}
                            style={{
                                padding: '1rem',
                                backgroundColor: theme?.colors?.background || '#f5f5f5',
                                border: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                                borderRadius: theme?.borderRadius || 8,
                                cursor: onOrderClick ? 'pointer' : 'default',
                                transition: 'all 0.2s',
                                textAlign: 'left',
                                fontFamily: 'inherit',
                                fontSize: '0.9rem',
                                minHeight: '80px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                                userSelect: 'none',
                                touchAction: 'manipulation'
                            }}
                            onClick={() => onOrderClick && onOrderClick(order.id)}
                            onTouchStart={(e) => {
                                const touch = e.touches[0];
                                setTouchStartPos({ x: touch.clientX, y: touch.clientY });
                            }}
                            onTouchEnd={(e) => {
                                if (onOrderClick) {
                                    const touch = e.changedTouches[0];
                                    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
                                    const deltaY = Math.abs(touch.clientY - touchStartPos.y);
                                    
                                    // Only trigger if movement was minimal (within 10px)
                                    if (deltaX < 10 && deltaY < 10) {
                                        e.preventDefault();
                                        onOrderClick(order.id);
                                    }
                                }
                            }}
                            onMouseEnter={e => {
                                if (onOrderClick) {
                                    e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#1976d2';
                                    e.currentTarget.style.color = theme?.colors?.surface || '#fff';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                }
                            }}
                            onMouseLeave={e => {
                                if (onOrderClick) {
                                    e.currentTarget.style.backgroundColor = theme?.colors?.background || '#f5f5f5';
                                    e.currentTarget.style.color = theme?.colors?.text || '#222';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }
                            }}
                        >
                            <div style={{ fontWeight: 'bold', fontSize: '1rem', color: theme?.colors?.text || '#222' }}>
                                {order.name}
                                {parentOrderNames[order.id] && <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary }}> ({t('from')} {parentOrderNames[order.id]})</span>}
                            </div>
                            <div style={{ color: theme?.colors?.textSecondary || '#666' }}>
                                {t('tableName')}: {order.table_name}
                            </div>
                            <div style={{ color: theme?.colors?.textSecondary || '#666' }}>
                                {order.item_count || 0} {t('items')}
                            </div>
                            {order.created && (
                                <div style={{ color: theme?.colors?.textSecondary || '#666', fontSize: '0.85rem' }}>
                                    {(() => {
                                        const date = new Date(order.created);
                                        const dateStr = date.toLocaleDateString('en-US', {
                                            day: 'numeric',
                                            month: 'short'
                                        }).replace(',', '').replace(' ', '-').toUpperCase();
                                        const timeStr = date.toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                        });
                                        return `${dateStr} ${timeStr}`;
                                    })()}
                                </div>
                            )}
                            <div style={{ color: theme?.colors?.textSecondary || '#666' }}>
                                {t('closed by')}: {(order.close_user_id ? order.close_user_name : t('unknown'))}
                            </div>

                            <div style={{ color: theme?.colors?.textSecondary || '#666' }}>
                                {t('orderTotal')}: {(order.total)}
                            </div>

                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
// ...existing code...
